import React from 'react';
import { TEnvironment } from '../Types/channel.types';
import { CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu, DropdownMenuItem } from 'Pages/GMS/ISR/UI-Model/components/Common/DropdownMenu';
import { toast } from 'react-toastify';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';

interface EnvironmentListProps {
    environments: TEnvironment[];
    isLoading: boolean;
    onAddEnvironment: () => void;
    onEditEnvironment: (environment: TEnvironment) => void;
    onDeleteEnvironment: (environment: TEnvironment) => void;
}

const EnvironmentList: React.FC<EnvironmentListProps> = ({
    environments,
    isLoading,
    onAddEnvironment,
    onEditEnvironment,
    onDeleteEnvironment
}) => {
    const getEnvironmentDropdownItems = (environment: TEnvironment): DropdownMenuItem[] => [
        {
            id: `edit-env-${environment.id}`,
            label: 'Edit',
            className: 'tw-text-[#172B4D]',
            onClick: () => onEditEnvironment(environment),
            icon: (
                <EditIcon />
            )
        },
        {
            id: `delete-env-${environment.id}`,
            label: 'Delete',
            onClick: () => onDeleteEnvironment(environment),
            className: 'tw-text-[#172B4D]',
            divider: true,
            icon: (
                <DeleteIcon />
            )
        }
    ];

    const handleCopyKey = (key: string) => {
        navigator.clipboard.writeText(key).then(() => {
            toast.success('Key copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy key');
        });
    };

    if (isLoading) {
        return (
            <div className="tw-p-4">
                <div className="tw-text-center tw-py-4">
                    <div className="tw-inline-flex tw-items-center">
                        <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading environments...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='tw-px-4'>
            <div className="tw-px-4 tw-pt-4 tw-pb-2 tw-text-xs tw-font-semibold tw-text-[#97A0AF]">
                Added Environment
            </div>

            <table className="tw-w-full tw-table-fixed">
                <tbody className=" tw-border-b tw-border-[#D1D1D6]">
                    {environments.map((env, index) => (
                        <tr key={env.id}>
                            <td className="tw-px-4 tw-py-3 tw-text-sm" style={{ width: '30%' }}>
                                <div className="tw-flex tw-items-center tw-gap-3">
                                    <span className="tw-font-semibold tw-text-sm tw-text-[#6D788C]">{env.name}</span>
                                    {env.isProduction && (
                                        <span className="tw-px-2 tw-py-0.5 tw-bg-gray-600 tw-text-white tw-text-xs tw-rounded tw-font-medium">
                                            Production
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-sm" style={{ width: '20%' }}>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-text-sm tw-text-gray-600">Key</span>
                                    <div
                                        onClick={() => {
                                            if (env.key) {
                                                handleCopyKey(env.key);
                                            }
                                        }}
                                        className="tw-cursor-pointer hover:tw-opacity-70"
                                    >
                                        <CopyIcon />
                                    </div>
                                </div>
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-right" style={{ width: '10%' }}>
                                <DropdownMenu
                                    items={getEnvironmentDropdownItems(env)}
                                    trigger={
                                        <button className="tw-p-1 hover:tw-bg-gray-100 tw-rounded">
                                            <OverFlowIcon />
                                        </button>
                                    }
                                    position="right"
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {environments.length < 3 ? (
                <div className="tw-px-4 tw-pb-4 tw-mt-2">
                    <button
                        onClick={onAddEnvironment}
                        className="outline-button"
                    >
                        Add Environment
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default EnvironmentList;